import React, { useState } from "react";

import {
  Grid,
  GridSize,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FiUser } from "react-icons/fi";
import { MdKeyboardArrowDown } from "react-icons/md";
import { RiMapPinLine } from "react-icons/ri";

import { Sales } from "../../../../../../../models/sales";
import FieldView from "../../../../../../Form/FieldView";
import GeneralCommentsComponent from "../../../../../../Generic/GeneralComments";
import { useTabComponentActions } from "../../useActions";
import useStyles from "./styles";

enum LocationType {
  ORIGIN = "origin",
  DESTINATION = "destination",
}

interface Props {
  job?: Sales;
}

const Details: React.FC<Props> = ({ job }) => {
  const { typeOfLocations, getLongAddressFromLocation, getCustomerFromLocation, getUserFullName } =
    useTabComponentActions();
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [delivery, setDelivery] = useState<boolean>(true);
  const [pickup, setPickup] = useState<boolean>(true);

  const renderCustomerInfo = (locationType?: LocationType.ORIGIN | LocationType.DESTINATION) => {
    if (job && locationType) {
      const customerFullName = getCustomerFromLocation(job[locationType]);

      return (
        customerFullName && (
          <div className={`${classes.addressSection} mb-2`}>
            <div className={classes.badge}>
              <FiUser size={16} color="#98a2b3" />
            </div>
            <span data-cy="customerName" className={classes.infoTextAccordion}>
              {customerFullName}
            </span>
          </div>
        )
      );
    }

    return null;
  };

  const renderLocationInfo = (locationType?: LocationType.ORIGIN | LocationType.DESTINATION) => {
    if (job && locationType) {
      return job[locationType]?.locations?.map((location, index: number) => {
        const longAddress = getLongAddressFromLocation(location);

        return (
          longAddress && (
            <div
              key={`${index}-location-${locationType}`}
              className={`${classes.addressSection} ${index > 0 && "mt-3"}`}
            >
              <div className={index === 0 ? classes.badge : classes.badgeWithoutBackground}>
                {index === 0 && <RiMapPinLine size={16} color="#98A2B3" />}
              </div>
              <span data-cy="longAddress" className={classes.infoTextAccordion}>
                {longAddress}
              </span>
            </div>
          )
        );
      });
    }

    return null;
  };

  const renderSimpleInfo = (label: string, grid: GridSize, value?: string) =>
    value && (
      <Grid item md={grid} lg={grid}>
        <FieldView label={label} value={value} />
      </Grid>
    );

  const renderAddressAccordion = (
    label: string,
    key: LocationType.ORIGIN | LocationType.DESTINATION,
    expandedValue: boolean,
    callback: (value: boolean) => void
  ) => (
    <Grid item xs={12} className={classes.accordionAddress} key={key}>
      <Accordion
        expanded={expandedValue}
        onChange={() => callback(!expandedValue)}
        className={classes.accordion}
      >
        <AccordionSummary className={classes.accordionLabel} expandIcon={<MdKeyboardArrowDown />}>
          {label}
        </AccordionSummary>
        <AccordionDetails>
          <div className="d-flex align-items-start flex-column">
            {renderCustomerInfo(key)}
            <div data-cy={`${label.replace(" ", "").toLowerCase()}-locationInfoSection`}>
              {renderLocationInfo(key)}
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );

  return (
    <Grid container direction="column">
      <Grid item container direction="row" xs={12}>
        {renderSimpleInfo(translate("jobType"), 4, job?.jobTypeName)}
        {renderSimpleInfo(translate("reference"), 4, job?.reference)}
        {renderSimpleInfo(translate("status"), 4, job?.jobSubStatus)}
      </Grid>
      <Grid item container direction="row" xs={12} className={classes.section}>
        {renderSimpleInfo(translate("consultant"), 6, getUserFullName(job?.user))}
        {renderSimpleInfo(translate("code"), 6, job?.user?.code)}
      </Grid>
      {job?.tag && (
        <Grid item container direction="row" xs={12} className={classes.section}>
          <Grid item md={6} lg={6}>
            <div className={classes.infoSection}>
              <label className={classes.infoLabel}>{translate("tag")}</label>
              <div className={classes.chipWrapper}>
                {job?.tag?.split(",").map((item) => (
                  <Chip key={item} label={item} className={classes.chip} />
                ))}
              </div>
            </div>
          </Grid>
        </Grid>
      )}
      <Grid item container direction="row" xs={12} className={classes.section}>
        {renderSimpleInfo(translate("mode"), 6, job?.mode)}
      </Grid>
      <Grid item container direction="column" xs={12} className={classes.section}>
        {typeOfLocations.map((type) => (
          <div key={type.label}>
            {renderAddressAccordion(
              type.label,
              type.value,
              type.value === LocationType.ORIGIN ? pickup : delivery,
              type.value === LocationType.ORIGIN ? setPickup : setDelivery
            )}
          </div>
        ))}
      </Grid>
      <Grid item xs={12} className={classes.section}>
        <GeneralCommentsComponent isVideoSurvey isViewMode comments={job?.quoteComments} />
      </Grid>
    </Grid>
  );
};

export default Details;
